/**
 * SASADM-0138 — GHL Marketplace app registration + installs tab
 * (saas-auth SASATH-0154 / SASATH-0288).
 *
 * A THIRD, distinct GHL record from the existing OIDC client (Connected Apps)
 * and tenant GHL integration tabs — saas-auth as an OAuth *client* to GHL,
 * holding per-location install tokens, product-scoped. SUPER_ADMIN-only
 * (the tab itself is superAdmin-gated by ProductDetail; every backend
 * endpoint here is `@authz.isSaasSuperAdmin`).
 *
 * SECRET HYGIENE (load-bearing). The client secret VALUE never crosses this
 * boundary in either direction — this surface carries only the Key Vault
 * NAME it lives under (`clientSecretVaultName`) plus `clientSecretSet`. The
 * vault name is a normal editable text field, ALWAYS sent explicitly on
 * every upsert (never omitted, never left to a "leave blank for default"
 * affordance) — omitting or blanking it makes the backend silently repoint a
 * working custom vault name to the derived convention, breaking installs at
 * token exchange (the "vault-name trap"; see `GhlMarketplaceAppService`).
 * After a successful upsert the form re-reads via `get()` rather than
 * trusting the response echo, so `clientSecretSet` reflects the freshly
 * stored vault name.
 *
 * 404 is a NORMAL response on both endpoints, never an error: on the
 * registration GET it means "not configured yet"; on `listInstalls` it means
 * SASATH-0288 (code pending) hasn't shipped — both resolve to an empty state.
 */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  GhlMarketplaceAppDto,
  GhlMarketplaceInstallDto,
  UpsertGhlMarketplaceAppRequest,
} from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { env } from '@/config/env';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

type TFn = ReturnType<typeof useCopy>['t'];

const DEFAULT_SCOPES =
  'workflows.readonly oauth.readonly oauth.write marketplace-installer-details.readonly';

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

type InstallState = 'revoked' | 'agency' | 'orphaned' | 'healthy';

// Discriminate on `userType` first, not on a null tenant alone — a COMPANY
// install always has a null tenant (it has no location to map), so testing
// only for that would report a healthy agency install as a fault.
function classifyInstall(install: GhlMarketplaceInstallDto): InstallState {
  if (install.status === 'REVOKED') return 'revoked';
  if (install.userType === 'COMPANY') return 'agency';
  if (install.userType === 'LOCATION' && install.tenantId == null) return 'orphaned';
  return 'healthy';
}

export interface GhlMarketplaceAppProps {
  productId: number;
  productSlug: string;
  productDisplayName: string;
}

export function GhlMarketplaceApp({
  productId,
  productSlug,
  productDisplayName,
}: GhlMarketplaceAppProps) {
  const { t } = useCopy();
  const snack = useSnackbar();

  const [app, setApp] = useState<GhlMarketplaceAppDto | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [installs, setInstalls] = useState<GhlMarketplaceInstallDto[] | null>(null);
  const [installsLoaded, setInstallsLoaded] = useState(false);
  const [installsError, setInstallsError] = useState<ApiError | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // On a genuine load failure, `app`/`installs` are left at their last known
  // value rather than reset to null — collapsing a transient error into the
  // unconfigured/"Register app" state would let an operator submit a fresh
  // upsert (with a `clientSecretVaultName` they don't know the real value of)
  // over what may still be a working registration, the vault-name trap by a
  // different route. Only a resolved (non-throwing) `null` — the real 404 —
  // means "not configured yet".
  async function loadApp() {
    setLoadError(null);
    try {
      const result = await apiAdmin.ghlMarketplaceApp.get(productId);
      setApp(result);
    } catch (err) {
      if (err instanceof ApiError) setLoadError(err);
    } finally {
      setLoaded(true);
    }
  }

  async function loadInstalls() {
    setInstallsError(null);
    try {
      const list = await apiAdmin.ghlMarketplaceApp.listInstalls(productId);
      setInstalls(list);
    } catch (err) {
      if (err instanceof ApiError) setInstallsError(err);
    } finally {
      setInstallsLoaded(true);
    }
  }

  useEffect(() => {
    // `loadApp`/`loadInstalls` setState synchronously before their first
    // `await`; wrap them in an inline async IIFE so the effect body holds no
    // synchronous setState (behavior identical) — same pattern as
    // ProductInternalKeys.tsx / TenantGhlIntegration.tsx.
    void (async () => {
      await Promise.all([loadApp(), loadInstalls()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await apiAdmin.ghlMarketplaceApp.delete(productId);
      snack.success(t('products.ghlMarketplace.deleted', 'GHL Marketplace app deleted.'));
      setConfirmDelete(false);
      setApp(null);
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
        >
          <Typography variant="h3">
            {t('products.ghlMarketplace.title', 'GHL Marketplace app')}
          </Typography>
          {loaded && app ? (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setFormOpen(true)}>
                {t('common.edit', 'Edit')}
              </Button>
              <Button size="small" color="error" onClick={() => setConfirmDelete(true)}>
                {t('common.delete', 'Delete')}
              </Button>
            </Stack>
          ) : null}
        </Stack>
        <ErrorBanner error={loadError} />
        {!loaded ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <CircularProgress size={16} />
            <Typography sx={{ color: 'text.secondary' }}>
              {t('common.loading', 'Loading')}
            </Typography>
          </Stack>
        ) : app ? (
          <Grid container spacing={2}>
            <Field label={t('products.ghlMarketplace.fields.clientId', 'Client ID')}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {app.clientId}
                </Typography>
                <CopyToClipboardButton value={app.clientId ?? ''} />
              </Stack>
            </Field>
            <Field
              label={t(
                'products.ghlMarketplace.fields.clientSecretVaultName',
                'Client secret vault name',
              )}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {app.clientSecretVaultName}
              </Typography>
            </Field>
            <Grid size={{ xs: 12 }}>
              {app.clientSecretSet ? (
                <Alert severity="success" variant="outlined">
                  {t('products.ghlMarketplace.secretResolved', 'Secret resolved')}
                </Alert>
              ) : (
                <Alert severity="warning">
                  <AlertTitle>
                    {t('products.ghlMarketplace.secretMissing', 'Secret missing')}
                  </AlertTitle>
                  <Typography variant="body2">
                    {t(
                      'products.ghlMarketplace.secretMissingConsequence',
                      'The client secret does not resolve from Key Vault. Installs will fail at token exchange.',
                    )}
                  </Typography>
                </Alert>
              )}
            </Grid>
            <Field label={t('products.ghlMarketplace.fields.redirectUri', 'Redirect URI')}>
              <Stack spacing={0.25}>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {app.redirectUri}
                  </Typography>
                  <CopyToClipboardButton value={app.redirectUri ?? ''} />
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t(
                    'products.ghlMarketplace.fields.redirectUriHint',
                    'Expected shape: https://<auth host>/api/oauth/crm/callback/<product-slug>',
                  )}
                </Typography>
              </Stack>
            </Field>
            <Field label={t('products.ghlMarketplace.fields.scopes', 'Scopes')}>
              <Typography variant="body2">{app.scopes}</Typography>
            </Field>
            <Field label={t('products.ghlMarketplace.fields.status', 'Status')}>
              <Typography variant="body2">{app.status}</Typography>
            </Field>
          </Grid>
        ) : loadError ? null : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {t(
                'products.ghlMarketplace.unconfigured',
                'This product does not talk to GoHighLevel until a Marketplace app is registered.',
              )}
            </Typography>
            <Button variant="contained" onClick={() => setFormOpen(true)}>
              {t('products.ghlMarketplace.register', 'Register app')}
            </Button>
          </>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('products.ghlMarketplace.installs.title', 'Installs')}
        </Typography>
        <ErrorBanner error={installsError} />
        {!installsLoaded ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <CircularProgress size={16} />
            <Typography sx={{ color: 'text.secondary' }}>
              {t('common.loading', 'Loading')}
            </Typography>
          </Stack>
        ) : installs === null ? null : installs.length === 0 ? (
          <Typography sx={{ color: 'text.secondary' }}>
            {t(
              'products.ghlMarketplace.installs.empty',
              'No installs yet. Install the app into a sub-account from the GHL Marketplace — installs appear here once the callback completes.',
            )}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('products.ghlMarketplace.installs.columns.userType', 'Type')}
                  </TableCell>
                  <TableCell>
                    {t('products.ghlMarketplace.installs.columns.companyId', 'Company')}
                  </TableCell>
                  <TableCell>
                    {t('products.ghlMarketplace.installs.columns.locationId', 'Location')}
                  </TableCell>
                  <TableCell>
                    {t('products.ghlMarketplace.installs.columns.status', 'Status')}
                  </TableCell>
                  <TableCell>
                    {t('products.ghlMarketplace.installs.columns.expiresAt', 'Expires')}
                  </TableCell>
                  <TableCell>
                    {t('products.ghlMarketplace.installs.columns.scopes', 'Scopes')}
                  </TableCell>
                  <TableCell>
                    {t('products.ghlMarketplace.installs.columns.tenant', 'Tenant')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {installs.map((install) => (
                  <InstallRow key={install.id} install={install} t={t} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <GhlMarketplaceAppForm
        open={formOpen}
        productId={productId}
        productSlug={productSlug}
        app={app}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          void loadApp();
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('products.ghlMarketplace.deleteTitle', 'Delete GHL Marketplace app?')}
        body={t(
          'products.ghlMarketplace.deleteBody',
          'This product stops authenticating to GoHighLevel, and existing install rows are orphaned.',
        )}
        severity="error"
        requireTypedConfirmation={productDisplayName}
        busy={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      {children}
    </Grid>
  );
}

function InstallRow({ install, t }: { install: GhlMarketplaceInstallDto; t: TFn }) {
  const state = classifyInstall(install);
  return (
    <>
      <TableRow>
        <TableCell>
          {install.userType === 'COMPANY'
            ? t('products.ghlMarketplace.installs.agency', 'Agency')
            : t('products.ghlMarketplace.installs.userTypes.LOCATION', 'Location')}
        </TableCell>
        <TableCell>{install.companyId ?? '—'}</TableCell>
        <TableCell>{install.locationId ?? '—'}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={t(`products.ghlMarketplace.installs.statuses.${install.status}`, install.status)}
            color={state === 'revoked' ? 'error' : 'success'}
            variant={state === 'revoked' ? 'filled' : 'outlined'}
          />
        </TableCell>
        <TableCell>{formatTimestamp(install.expiresAt)}</TableCell>
        <TableCell>{install.scopes}</TableCell>
        <TableCell>
          {install.tenantId != null ? (
            <Typography
              component={RouterLink}
              to={`/tenants/${install.tenantId}/overview`}
              variant="body2"
            >
              {install.tenantSlug ?? `#${install.tenantId}`}
            </Typography>
          ) : (
            '—'
          )}
        </TableCell>
      </TableRow>
      {state === 'revoked' ? (
        <TableRow>
          <TableCell colSpan={7} sx={{ borderTop: 'none', pt: 0 }}>
            <Alert severity="error" variant="outlined">
              {t(
                'products.ghlMarketplace.installs.revoked',
                'This install was revoked after a token refresh failed. Reinstall the app in this sub-account to restore it.',
              )}
            </Alert>
          </TableCell>
        </TableRow>
      ) : null}
      {state === 'orphaned' ? (
        <TableRow>
          <TableCell colSpan={7} sx={{ borderTop: 'none', pt: 0 }}>
            <Alert severity="warning" variant="outlined">
              {t(
                'products.ghlMarketplace.installs.orphaned',
                "No tenant claims this location. Check the intended tenant's Integration Settings location id.",
              )}
            </Alert>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

interface GhlMarketplaceAppFormProps {
  open: boolean;
  productId: number;
  productSlug: string;
  app: GhlMarketplaceAppDto | null;
  onClose: () => void;
  onSaved: () => void;
}

function GhlMarketplaceAppForm({
  open,
  productId,
  productSlug,
  app,
  onClose,
  onSaved,
}: GhlMarketplaceAppFormProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const isEdit = app != null;

  const [clientId, setClientId] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [scopes, setScopes] = useState('');
  const [clientSecretVaultName, setClientSecretVaultName] = useState('');
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset/prefill the form on the open transition via a render-phase state
  // adjustment (React's "adjust state while rendering" pattern) instead of a
  // synchronous setState in an effect — same result, no inter-render flash.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setSaveError(null);
      setValidationError(null);
      setClientId(app?.clientId ?? '');
      setRedirectUri(
        app?.redirectUri ?? `${env.authBaseUrl}/api/oauth/crm/callback/${productSlug}`,
      );
      setScopes(app?.scopes ?? DEFAULT_SCOPES);
      setClientSecretVaultName(app?.clientSecretVaultName ?? '');
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setValidationError(null);

    const trimmedRedirect = redirectUri.trim();
    if (trimmedRedirect.includes('/ghl/')) {
      setValidationError(
        t(
          'products.ghlMarketplace.errors.redirectUriGhlSegment',
          "GHL's white-label filter rejects any redirect URL containing 'ghl'. Use /crm/ — this is why installs silently failed before.",
        ),
      );
      return;
    }
    if (!/^https:\/\/.+/.test(trimmedRedirect)) {
      setValidationError(
        t('products.ghlMarketplace.errors.redirectUriInvalid', 'Enter an absolute https:// URL.'),
      );
      return;
    }

    setSaving(true);
    try {
      // `clientSecretVaultName` is ALWAYS sent explicitly (decision 3) — an
      // edit that only changes scopes must not repoint the stored secret.
      const req: UpsertGhlMarketplaceAppRequest = {
        clientId: clientId.trim(),
        redirectUri: trimmedRedirect,
        scopes: scopes.trim(),
        clientSecretVaultName: clientSecretVaultName.trim(),
      };
      await apiAdmin.ghlMarketplaceApp.upsert(productId, req);
      snack.success(t('products.ghlMarketplace.saved', 'GHL Marketplace app saved.'));
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? t('products.ghlMarketplace.editTitle', 'Edit GHL Marketplace app')
          : t('products.ghlMarketplace.registerTitle', 'Register GHL Marketplace app')}
      </DialogTitle>
      <DialogContent>
        <ErrorBanner message={validationError} error={saveError} />
        <Box
          component="form"
          id="ghl-marketplace-app-form"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label={t('products.ghlMarketplace.fields.clientId', 'Client ID')}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
            <TextField
              fullWidth
              required
              label={t('products.ghlMarketplace.fields.redirectUri', 'Redirect URI')}
              value={redirectUri}
              onChange={(e) => setRedirectUri(e.target.value)}
              helperText={t(
                'products.ghlMarketplace.fields.redirectUriHint',
                'Expected shape: https://<auth host>/api/oauth/crm/callback/<product-slug>',
              )}
            />
            <TextField
              fullWidth
              required
              label={t('products.ghlMarketplace.fields.scopes', 'Scopes')}
              value={scopes}
              onChange={(e) => setScopes(e.target.value)}
            />
            <TextField
              fullWidth
              label={t(
                'products.ghlMarketplace.fields.clientSecretVaultName',
                'Client secret vault name',
              )}
              value={clientSecretVaultName}
              onChange={(e) => setClientSecretVaultName(e.target.value)}
              placeholder={t(
                'products.ghlMarketplace.fields.clientSecretVaultNamePlaceholder',
                'saasauth-ghl-app-{{slug}}-client-secret',
                { slug: productSlug },
              )}
              helperText={t(
                'products.ghlMarketplace.fields.clientSecretVaultNameHelper',
                'Always sent as-is on save. The convention shown as placeholder text is not applied unless typed.',
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button form="ghl-marketplace-app-form" type="submit" variant="contained" disabled={saving}>
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
